import { Autocomplete, Box, TextField } from "@mui/material";
import ErrorWarningIcon from "components/common/InputErrorWarningIcon";
import { useFormContext, Controller } from "react-hook-form";

import cites from "../SignUp/cites.json";
import states from "../SignUp/states.json";

function AddressForm() {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const selectedState = watch("state");
  const selectedCity = watch("city");

  const getError = (fieldName: string): { message?: string } | undefined => {
    return errors[fieldName] as { message?: string } | undefined;
  };

  const errorIcon = (fieldName: string) => {
    const error = getError(fieldName);
    const isError = !!error;
    if ((fieldName === "city" || fieldName === "state") && isError) {
      return {
        popupIcon: <ErrorWarningIcon hasError={isError} />,
      };
    }
    return {
      endAdornment: <ErrorWarningIcon hasError={isError} />,
    };
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <TextField
              label="Address"
              variant="outlined"
              className="w-full"
              error={!!getError("address")}
              helperText={
                (getError("address") as { message?: string })?.message
              }
              slotProps={{
                input: errorIcon("address"),
                htmlInput: {
                  maxLength: 255,
                },
              }}
              {...field}
            />
          )}
        />

        <Controller
          name="street"
          control={control}
          render={({ field }) => {
            const isStreetDisabled = !selectedCity;
            return (
              <TextField
                label="Street"
                variant="outlined"
                className="w-full"
                disabled={isStreetDisabled}
                error={!!getError("street")}
                helperText={
                  (getError("street") as { message?: string })?.message
                }
                classes={{
                  root: isStreetDisabled ? "opacity-50" : "",
                }}
                slotProps={{
                  input: errorIcon("street"),
                  htmlInput: {
                    maxLength: 255,
                  },
                }}
                {...field}
              />
            );
          }}
        />
      </Box>

      <Controller
        name="city"
        control={control}
        render={({ field }) => {
          const cityOptions = selectedState
            ? (cites as Record<string, string[]>)[selectedState] || []
            : [];
          const isCityDisabled = !selectedState;

          return (
            <Autocomplete
              disablePortal
              disabled={isCityDisabled}
              options={cityOptions}
              className="w-full"
              getOptionLabel={(option) => option}
              onChange={(_, value) => field.onChange(value)}
              value={field.value || null}
              classes={{
                root: isCityDisabled ? "opacity-50" : "",
              }}
              {...errorIcon("city")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  variant="outlined"
                  error={!!getError("city")}
                  helperText={
                    (getError("city") as { message?: string })?.message
                  }
                />
              )}
            />
          );
        }}
      />

      <Controller
        name="state"
        control={control}
        render={({ field }) => {
          return (
            <Autocomplete
              disablePortal
              options={states}
              className="w-full"
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.abbreviation}
              {...errorIcon("state")}
              onChange={(_, value) => {
                field.onChange(value?.label);
                // Clear city field when state changes
                if (field.value !== value?.label) {
                  setValue("city", "");
                }
              }}
              value={
                states.find((state) => state.label === field.value) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  variant="outlined"
                  error={!!getError("state")}
                  helperText={
                    (getError("state") as { message?: string })?.message
                  }
                />
              )}
            />
          );
        }}
      />

      <Controller
        name="zip"
        control={control}
        render={({ field }) => (
          <TextField
            label="Zip"
            variant="outlined"
            className="w-full"
            error={!!getError("zip")}
            helperText={(getError("zip") as { message?: string })?.message}
            slotProps={{
              input: errorIcon("zip"),
              htmlInput: {
                maxLength: 5,
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
            }}
            {...field}
          />
        )}
      />
    </Box>
  );
}

export default AddressForm;
